import { Button, Typography, Box, alpha, useTheme, Stack } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { DeleteOutline } from "@mui/icons-material";
import DialogBase from "./DialogBase";
import { UtilityService } from "../../services/UtilityService";
import { LabelsService } from "../../services/LabelsService";

export default function LabelDeleteDialog(props) {
  const { t } = useTranslation("common");
  const theme = useTheme();

  const { open, handleClose, label } = props;
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!label || !label.id) {
      UtilityService.updateSnackbarMessage(
        t("common.error_missing_id"),
        "error"
      );
      return;
    }

    setIsDeleting(true);
    try {
      const result = await LabelsService.deleteLabel(label.id);

      if (result) {
        UtilityService.updateSnackbarMessage(
          t("common.label_deleted_successfully")
        );

        // Wait for the snackbar to show before closing
        setTimeout(() => {
          handleClose();
        }, 1000);
      } else {
        throw new Error(t("labels.failed_to_delete"));
      }
    } catch (error) {
      UtilityService.updateSnackbarMessage(
        `Error: Failed to delete label - ${error.message}`
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DialogBase
      open={open}
      title={t("labels.delete")}
      handleClose={handleClose}
      icon={<DeleteOutline fontSize="medium" color="error" />}
      actions={
        <>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 500,
              px: 3,
              color:
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
              borderColor: alpha(
                theme.palette.mode === "dark"
                  ? "#00c9a7"
                  : theme.palette.secondary.main,
                0.5
              ),
              "&:hover": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                backgroundColor: alpha(
                  theme.palette.mode === "dark"
                    ? "#00c9a7"
                    : theme.palette.secondary.main,
                  0.05
                ),
              },
            }}
          >
            {t("common.cancel")}
          </Button>

          <Button
            variant="contained"
            onClick={handleDelete}
            disabled={isDeleting}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              py: 1,
              px: 3,
              fontWeight: 500,
              backgroundColor: theme.palette.error.main,
              color: "#fff",
              "&:hover": {
                backgroundColor: alpha(theme.palette.error.main, 0.9),
              },
            }}
          >
            {isDeleting ? t("common.deleting") : t("common.delete")}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Box
          sx={{
            p: 2.5,
            bgcolor: alpha(theme.palette.error.main, 0.05),
            borderRadius: "12px",
            border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 600,
              color: theme.palette.error.main,
              mb: 1,
            }}
          >
            {t("labels.delete_warning")}
          </Typography>

          <Typography variant="body2">
            {t("labels.delete_confirmation", {
              name: label?.name || "this label",
            })}
          </Typography>
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t("common.delete_irreversible")}
        </Typography>
      </Stack>
    </DialogBase>
  );
}
